import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Save, User, CreditCard, MapPin, LogOut, Building2 } from "lucide-react";
import api, { handleApiError, formatCPF, formatPhone, formatCEP } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import MFASetup from "@/components/MFASetup";
import KycBanner from "@/components/KycBanner";
import ChangePassword from "@/components/ChangePassword";
import ProfileProgress from "@/components/ProfileProgress";
import BankCombobox from "@/components/BankCombobox";
import { isCompeFormat } from "@/data/banks";

const MARITAL_OPTIONS = [
  { value: "solteiro", label: "Solteiro(a)" },
  { value: "casado", label: "Casado(a)" },
  { value: "divorciado", label: "Divorciado(a)" },
  { value: "viuvo", label: "Viúvo(a)" },
  { value: "separado", label: "Separado(a)" },
];

const BANK_TYPE_OPTIONS = [
  { value: "CC", label: "Conta Corrente" },
  { value: "CP", label: "Conta Poupança" },
  { value: "SA", label: "Conta Salário" },
];

const BR_STATES = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

const REQ = <span className="text-red-400">*</span>;

export default function EmployeeProfile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const fetchProfile = useCallback(async () => {
    try {
      const res = await api.get("/profile");
      setProfile(res.data);
      setForm(res.data);
    } catch { toast.error("Erro ao carregar perfil"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const updateField = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const fetchCep = useCallback(async (cep) => {
    const digits = (cep || "").replace(/\D/g, "");
    if (digits.length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (data.erro) return;
      setForm(prev => ({
        ...prev,
        address_street: data.logradouro || prev.address_street,
        address_neighborhood: data.bairro || prev.address_neighborhood,
        address_city: data.localidade || prev.address_city,
        address_state: data.uf || prev.address_state,
        address_complement: data.complemento || prev.address_complement,
      }));
      toast.success("Endereço preenchido pelo CEP");
    } catch { /* silent */ }
  }, []);

  // Auto-fetch address when CEP reaches 8 digits
  useEffect(() => {
    const digits = (form.address_zip_code || "").replace(/\D/g, "");
    if (digits.length === 8) {
      fetchCep(form.address_zip_code);
    }
  }, [form.address_zip_code, fetchCep]);

  const handleSave = async () => {
    const missing = [];
    const isPix = (form.payment_method || "pix") === "pix";
    if (!form.id_number?.trim()) missing.push("RG");
    if (!form.issuing_agency?.trim()) missing.push("Órgão Expedidor");
    if (!form.nationality?.trim()) missing.push("Nacionalidade");
    if (!form.marital_status) missing.push("Estado Civil");
    if (!form.occupation?.trim()) missing.push("Ocupação");
    if (!form.cellphone?.trim()) missing.push("Celular");
    if (!isPix) {
      if (!form.bank_code?.trim()) missing.push("Banco");
      else if (!isCompeFormat(form.bank_code)) missing.push("Banco (use o código COMPE de 3 dígitos)");
      if (!form.bank_type) missing.push("Tipo de Conta");
      if (!form.bank_account?.trim()) missing.push("Conta");
      if (!form.bank_agency?.trim()) missing.push("Agência");
    }
    if (!form.address_zip_code?.trim()) missing.push("CEP");
    if (!form.address_street?.trim()) missing.push("Rua");
    if (!form.address_number?.trim()) missing.push("Número");
    if (!form.address_neighborhood?.trim()) missing.push("Bairro");
    if (!form.address_city?.trim()) missing.push("Cidade");
    if (!form.address_state) missing.push("Estado");
    if (missing.length) {
      toast.error(`Campos obrigatórios: ${missing.slice(0, 5).join(", ")}${missing.length > 5 ? "..." : ""}`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        id_number: form.id_number || "", issuing_agency: form.issuing_agency || "",
        nationality: form.nationality || "", marital_status: form.marital_status || "",
        occupation: form.occupation || "", cellphone: form.cellphone || "",
        trade_name: form.trade_name || "",
        phone: form.phone || "",
        bank_code: form.bank_code || "", bank_type: form.bank_type || "",
        bank_account: form.bank_account || "", bank_agency: form.bank_agency || "",
        bank_key_pix: form.bank_key_pix || "",
        bank_key_pix_type: form.bank_key_pix_type || "",
        payment_method: form.payment_method || "pix",
        address_zip_code: form.address_zip_code || "", address_street: form.address_street || "",
        address_number: form.address_number || "", address_complement: form.address_complement || "",
        address_neighborhood: form.address_neighborhood || "", address_city: form.address_city || "",
        address_state: form.address_state || "", address_country: form.address_country || "Brasil",
      };
      if (isPix) {
        const digits = (profile?.cpf_cnpj || "").replace(/\D/g, "");
        payload.bank_key_pix = digits;
        payload.bank_key_pix_type = profile?.type === "PJ" ? "cnpj" : "cpf";
      }
      const res = await api.put("/profile", payload);
      setProfile(res.data);
      toast.success("Perfil atualizado!");
    } catch (err) {
      // `handleApiError` e não `err.response?.data?.detail` (A-R3e-xv): o
      // `ProfileUpdate` ganhou `AfterValidator` em `bank_agency`/`bank_account`,
      // e o `detail` de um 422 do pydantic é um ARRAY — o `||` caía no fallback
      // e a mensagem que ensina o formato SUMIA.
      toast.error(handleApiError(err, "Erro ao salvar"));
    }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-12"><div className="loading-spinner w-6 h-6" /></div>;

  const bankLockedByCompany = profile?.bank_locked_by_company === true;

  const isPJ = profile?.type === "PJ";

  return (
    <div className="space-y-6 animate-fade-in">
      <h2 className="text-lg font-semibold">Meu Perfil</h2>

      <ProfileProgress profile={form} />

      {/* Avatar + basic info */}
      <Card className="border-white/5">
        <CardContent className="p-5">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-blue-600/20 flex items-center justify-center text-blue-400 text-xl font-bold shrink-0">
              {user?.name?.charAt(0)}
            </div>
            <div className="min-w-0">
              <p className="font-semibold truncate">{profile?.name}</p>
              <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
              <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-semibold ${isPJ ? "bg-purple-500/15 text-purple-400" : "bg-blue-500/15 text-blue-400"}`}>
                {isPJ ? "Pessoa Jurídica" : "Pessoa Física"}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Multiple employer companies (if applicable) */}
      {Array.isArray(profile?.companies) && profile.companies.length > 1 && (
        <Card className="border-blue-500/20 bg-blue-500/5" data-testid="employer-companies-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-400" /> Empresas pagadoras ({profile.companies.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-xs">
            {profile.companies.map((c) => (
              <div key={c.employee_id}
                data-testid={`employer-company-${c.employee_id}`}
                className="flex items-center justify-between p-2 rounded-lg bg-black/20 border border-white/5">
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{c.company_name || "—"}</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {c.department || "Sem departamento"}
                    {c.termination_date ? ` · Desligado em ${c.termination_date}` : ""}
                  </p>
                </div>
                {c.is_primary && (
                  <span className="text-[10px] font-medium text-blue-400 bg-blue-500/10 border border-blue-500/20 px-2 py-0.5 rounded-full shrink-0 ml-2">
                    Principal
                  </span>
                )}
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground pt-1">
              Os recebíveis de todas as suas empresas aparecem juntos na aba Recebíveis.
            </p>
          </CardContent>
        </Card>
      )}

      <KycBanner />

      {/* Personal Data */}
      <Card className="border-white/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2"><User className="w-4 h-4 text-blue-400" /> Dados Pessoais</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Read-only identity fields */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">{isPJ ? "Razão Social (fixo)" : "Nome Completo (fixo)"}</Label>
              <Input value={profile?.name || ""} disabled className="h-9 bg-white/[0.02] border-white/5 text-muted-foreground" data-testid="profile-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">{isPJ ? "CNPJ (fixo)" : "CPF (fixo)"}</Label>
              <Input value={profile?.cpf_cnpj ? formatCPF(profile.cpf_cnpj) : ""} disabled className="h-9 bg-white/[0.02] border-white/5 text-muted-foreground" data-testid="profile-cpf" />
            </div>
          </div>
          {isPJ && profile?.socio_name && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Sócio Representante (fixo)</Label>
                <Input value={profile.socio_name || ""} disabled className="h-9 bg-white/[0.02] border-white/5 text-muted-foreground" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">CPF do Sócio (fixo)</Label>
                <Input value={profile.socio_cpf ? formatCPF(profile.socio_cpf) : ""} disabled className="h-9 bg-white/[0.02] border-white/5 text-muted-foreground" />
              </div>
            </div>
          )}

          {/* Editable fields */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">RG {REQ}</Label>
              <Input value={form.id_number || ""} onChange={(e) => updateField("id_number", e.target.value)}
                placeholder="123456789" className="h-9 bg-black/20 border-white/10" data-testid="profile-rg" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Órgão Expedidor {REQ}</Label>
              <Input value={form.issuing_agency || ""} onChange={(e) => updateField("issuing_agency", e.target.value)}
                placeholder="SSP/SP" className="h-9 bg-black/20 border-white/10" data-testid="profile-issuing-agency" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Nacionalidade {REQ}</Label>
              <Input value={form.nationality || ""} onChange={(e) => updateField("nationality", e.target.value)}
                placeholder="Brasileiro" className="h-9 bg-black/20 border-white/10" data-testid="profile-nationality" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Estado Civil {REQ}</Label>
              <Select value={form.marital_status || ""} onValueChange={(v) => updateField("marital_status", v)}>
                <SelectTrigger className="h-9 bg-black/20 border-white/10" data-testid="profile-marital-status">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {MARITAL_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Ocupação {REQ}</Label>
              <Input value={form.occupation || ""} onChange={(e) => updateField("occupation", e.target.value)}
                placeholder="Desenvolvedor" className="h-9 bg-black/20 border-white/10" data-testid="profile-occupation" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Celular {REQ}</Label>
              <Input value={form.cellphone || ""} onChange={(e) => updateField("cellphone", formatPhone(e.target.value))}
                placeholder="(11) 99999-9999" maxLength={15} className="h-9 bg-black/20 border-white/10" data-testid="profile-cellphone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs">Email {REQ}</Label>
              <Input value={user?.email || ""} disabled className="h-9 bg-white/[0.02] border-white/5 text-muted-foreground" data-testid="profile-email" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Forma de Recebimento */}
      <Card className="border-white/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2"><CreditCard className="w-4 h-4 text-emerald-400" /> Forma de Recebimento</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {bankLockedByCompany && (
            <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
              <p className="text-xs text-orange-400 font-medium">Dados de pagamento gerenciados pela empresa</p>
              <p className="text-[10px] text-muted-foreground mt-1">Solicite alterações ao seu gestor.</p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">Método de Pagamento</Label>
            <Select value={form.payment_method || "pix"} disabled={bankLockedByCompany} onValueChange={(v) => updateField("payment_method", v)}>
              <SelectTrigger className="h-9 bg-black/20 border-white/10" data-testid="profile-payment-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pix">Chave PIX (CPF/CNPJ)</SelectItem>
                <SelectItem value="bank">Conta Bancária</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {(form.payment_method || "pix") === "pix" ? (
            <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/10">
              <p className="text-sm text-blue-400 font-medium">
                {profile?.type === "PJ" ? "CNPJ da empresa" : "CPF"} será usado como chave PIX
              </p>
              <p className="text-xs text-muted-foreground mt-1 font-mono">
                {profile?.cpf_cnpj || "Documento não cadastrado"}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Banco {REQ}</Label>
                  <BankCombobox value={form.bank_code || ""} onChange={(v) => updateField("bank_code", v)} disabled={bankLockedByCompany} testId="profile-bank-code" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Tipo de Conta {REQ}</Label>
                  <Select value={form.bank_type || ""} disabled={bankLockedByCompany} onValueChange={(v) => updateField("bank_type", v)}>
                    <SelectTrigger className="h-9 bg-black/20 border-white/10" data-testid="profile-bank-type">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {BANK_TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Agência {REQ}</Label>
                  <Input value={form.bank_agency || ""} onChange={(e) => updateField("bank_agency", e.target.value)}
                    placeholder="0001" className="h-9 bg-black/20 border-white/10" data-testid="profile-bank-agency" disabled={bankLockedByCompany} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Conta {REQ}</Label>
                  <Input value={form.bank_account || ""} onChange={(e) => updateField("bank_account", e.target.value)}
                    placeholder="123456-7" className="h-9 bg-black/20 border-white/10" data-testid="profile-bank-account" disabled={bankLockedByCompany} />
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Address */}
      <Card className="border-white/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2"><MapPin className="w-4 h-4 text-yellow-400" /> Endereço {REQ}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">CEP {REQ}</Label>
              <Input value={form.address_zip_code || ""} 
                onChange={(e) => updateField("address_zip_code", formatCEP(e.target.value))}
                onBlur={(e) => fetchCep(e.target.value)}
                placeholder="09961-050" maxLength={9} className="h-9 bg-black/20 border-white/10" data-testid="profile-cep" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs">Rua {REQ}</Label>
              <Input value={form.address_street || ""} onChange={(e) => updateField("address_street", e.target.value)}
                className="h-9 bg-black/20 border-white/10" data-testid="profile-street" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Número {REQ}</Label>
              <Input value={form.address_number || ""} onChange={(e) => updateField("address_number", e.target.value)}
                className="h-9 bg-black/20 border-white/10" data-testid="profile-number" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs">Complemento</Label>
              <Input value={form.address_complement || ""} onChange={(e) => updateField("address_complement", e.target.value)}
                className="h-9 bg-black/20 border-white/10" data-testid="profile-complement" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Bairro {REQ}</Label>
              <Input value={form.address_neighborhood || ""} onChange={(e) => updateField("address_neighborhood", e.target.value)}
                className="h-9 bg-black/20 border-white/10" data-testid="profile-neighborhood" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Cidade {REQ}</Label>
              <Input value={form.address_city || ""} onChange={(e) => updateField("address_city", e.target.value)}
                className="h-9 bg-black/20 border-white/10" data-testid="profile-city" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Estado {REQ}</Label>
              <Select value={form.address_state || ""} onValueChange={(v) => updateField("address_state", v)}>
                <SelectTrigger className="h-9 bg-black/20 border-white/10" data-testid="profile-state">
                  <SelectValue placeholder="UF" />
                </SelectTrigger>
                <SelectContent>
                  {BR_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">País</Label>
              <Input value={form.address_country || "Brasil"} onChange={(e) => updateField("address_country", e.target.value)}
                className="h-9 bg-black/20 border-white/10" data-testid="profile-country" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Button data-testid="save-profile-btn" onClick={handleSave} disabled={saving}
        className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold">
        {saving ? <div className="loading-spinner" /> : <><Save className="w-4 h-4 mr-2" /> Salvar Perfil</>}
      </Button>

      <ChangePassword />

      <Separator className="bg-white/5" />

      <Button data-testid="logout-btn" variant="outline" onClick={() => { logout(); navigate("/login"); }}
        className="w-full h-11 border-red-500/20 text-red-400 hover:bg-red-500/10 hover:text-red-300">
        <LogOut className="w-4 h-4 mr-2" /> Sair da conta
      </Button>

      <div className="h-4" />
    </div>
  );
}
